import React, { useCallback, useEffect, useState } from 'react';
import { Download, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import {
  collectAILearningExamples,
  downloadAILearningDataset,
  getAILearningExamples,
  getAILearningStatus,
  reviewAILearningExample,
} from '../../services/api.admin';
import { getApiErrorMessage } from '../../lib/apiErrors';

const AILearningManager = () => {
  const [status, setStatus] = useState(null);
  const [examples, setExamples] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [statusResponse, examplesResponse] = await Promise.all([
        getAILearningStatus(),
        getAILearningExamples({ review_status: 'pending', limit: 25 }),
      ]);
      setStatus(statusResponse.data);
      setExamples(examplesResponse.data?.examples || []);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load AI learning review queue'));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const collect = async () => {
    setBusy(true);
    try {
      const { data } = await collectAILearningExamples();
      toast.success(`Reviewed ${data.examined || 0} closed decisions; added ${data.created || 0} examples`);
      await load();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to collect closed AI outcomes'));
    } finally {
      setBusy(false);
    }
  };

  const review = async (exampleId, reviewStatus) => {
    setBusy(true);
    try {
      await reviewAILearningExample(exampleId, reviewStatus);
      await load();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to review learning example'));
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    try {
      const response = await downloadAILearningDataset();
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'tradopro-ai-research-approved.jsonl';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to export approved dataset'));
    }
  };

  return (
    <Card className="border-zinc-800 bg-zinc-900">
      <CardHeader>
        <CardTitle>AI Knowledge Repository</CardTitle>
        <CardDescription>
          Approved closed outcomes are retrieved as live RAG evidence. JSONL export supports optional offline LoRA training; production never trains or promotes itself.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="rounded-md border border-zinc-800 bg-zinc-950/60 px-3 py-3">
            <div className="text-xs uppercase tracking-[0.14em] text-zinc-500">RAG runtime</div>
            <div className="mt-1 text-sm font-semibold text-white">{status?.rag_status || 'Checking'}</div>
            <div className="mt-1 text-xs text-zinc-500">Active only after reviewed examples are approved.</div>
          </div>
          <div className="rounded-md border border-zinc-800 bg-zinc-950/60 px-3 py-3">
            <div className="text-xs uppercase tracking-[0.14em] text-zinc-500">LoRA runtime</div>
            <div className="mt-1 text-sm font-semibold text-white">{status?.lora_status || 'Checking'}</div>
            <div className="mt-1 text-xs text-zinc-500">Adapter and provider model must match.</div>
          </div>
          <div className="rounded-md border border-zinc-800 bg-zinc-950/60 px-3 py-3">
            <div className="text-xs uppercase tracking-[0.14em] text-zinc-500">Ready to collect</div>
            <div className="mt-1 text-xl font-semibold text-white">{status?.collectable_closed_decisions || 0}</div>
            <div className="mt-1 text-xs text-zinc-500">Daily report runs at 5:10 PM ET.</div>
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          {['pending', 'approved', 'rejected'].map((key) => (
            <div key={key} className="rounded-md border border-zinc-800 bg-zinc-950/60 px-3 py-3">
              <div className="text-xs uppercase tracking-[0.14em] text-zinc-500">{key}</div>
              <div className="mt-1 text-xl font-semibold text-white">{status?.examples?.[key] || 0}</div>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={collect} disabled={busy}><RefreshCw className="mr-2 h-4 w-4" />Collect closed outcomes</Button>
          <Button variant="outline" onClick={download}><Download className="mr-2 h-4 w-4" />Export for optional LoRA</Button>
        </div>
        <div className="space-y-2">
          {examples.length ? examples.map((example) => (
            <div key={example.id} className="rounded-md border border-zinc-800 bg-zinc-950/50 p-3">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline">{example.example_type}</Badge>
                    <span className="text-sm font-medium text-white">{example.action || 'Decision'}</span>
                    <span className={`text-sm font-semibold ${(example.realized_pnl || 0) >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                      {example.realized_pnl == null ? 'P&L unavailable' : `P&L ${Number(example.realized_pnl).toFixed(2)}`}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-zinc-500">{example.reason_code || 'No model reason'} · {example.execution_job_count || 0} execution jobs</div>
                  {example.rationale ? <div className="mt-2 line-clamp-2 text-xs text-zinc-400">{example.rationale}</div> : null}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => review(example.id, 'rejected')}>Reject</Button>
                  <Button size="sm" disabled={busy} onClick={() => review(example.id, 'approved')}>Approve</Button>
                </div>
              </div>
            </div>
          )) : (
            <div className="rounded-md border border-dashed border-zinc-800 px-4 py-6 text-center text-sm text-zinc-500">No pending examples.</div>
          )}
        </div>
        {status?.model_versions?.length ? (
          <div className="border-t border-zinc-800 pt-3 text-xs text-zinc-500">
            Latest registered model: {status.model_versions[0].version} · {status.model_versions[0].status}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default AILearningManager;
